import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);

/**
 * Detects the picker type based on the day.js format string
 * @param {string} format - Day.js format string
 * @returns {string} - 'date', 'time', or 'datetime'
 */
export const detectPickerType = (format) => {
  if (!format || typeof format !== 'string') {
    return 'date'; // Default to date picker
  }
  const hasDate = /[YMD]/.test(format);
  const hasTime = /[hHmsa]/.test(format);
  
  if (hasDate && hasTime) return 'datetime';
  if (hasTime) return 'time';
  return 'date';
};

/**
 * Converts a stored timestamp to a Date object that will display correctly in react-datepicker
 * for the specified timezone. This "shifts" the Date object to trick react-datepicker into
 * showing the correct timezone date/time.
 * @param {number} timestamp - Millisecond timestamp from storage
 * @param {string} timezone - IANA timezone identifier (e.g., 'America/New_York')
 * @param {string} format - Day.js format string to determine picker type
 * @returns {Date} - Date object adjusted for picker display
 */
export const convertTimestampToPickerDate = (timestamp, timezone, format) => {
  // If no timestamp provided, return current date
  if (!timestamp || typeof timestamp !== 'number') {
    return new Date();
  }

  // If no timezone provided, return timestamp as Date
  if (!timezone) {
    return new Date(timestamp);
  }

  const pickerType = detectPickerType(format);
  const dayjsObj = dayjs(timestamp);
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // If user is already in the target timezone, no conversion needed
  if (userTimezone === timezone) {
    return new Date(timestamp);
  }

  switch (pickerType) {
    case 'time': {
      // For time-only: Extract time components and apply to today in user's timezone
      const timeInTargetTimezone = dayjsObj.tz(timezone);
      const todayInUserTimezone = dayjs().tz(userTimezone).startOf('day');
      const adjustedTime = todayInUserTimezone
        .add(timeInTargetTimezone.hour(), 'hours')
        .add(timeInTargetTimezone.minute(), 'minutes')
        .add(timeInTargetTimezone.second(), 'seconds');
      
      return adjustedTime.toDate();
    }
    
    case 'date': {
      // For date-only: Convert the date to user's timezone while preserving the date
      const dateInTargetTimezone = dayjsObj.tz(timezone).startOf('day');
      const adjustedDate = dayjs.tz(dateInTargetTimezone.format('YYYY-MM-DD'), userTimezone);
      
      return adjustedDate.toDate();
    }
    
    case 'datetime': {
      // For datetime: Extract the date/time components from the target timezone
      // and create a Date object in user's timezone with those same components
      const datetimeInTargetTimezone = dayjsObj.tz(timezone);
      const year = datetimeInTargetTimezone.year();
      const month = datetimeInTargetTimezone.month();
      const date = datetimeInTargetTimezone.date();
      const hour = datetimeInTargetTimezone.hour();
      const minute = datetimeInTargetTimezone.minute();
      const second = datetimeInTargetTimezone.second();
      
      // Create a new Date object in user's timezone with the same components
      const adjustedDatetime = dayjs().tz(userTimezone)
      .year(year)
      .month(month)
      .date(date)
      .hour(hour)
      .minute(minute)
      .second(second);
    
      
      return adjustedDatetime.toDate();
    }
    
    default:
      return new Date(timestamp);
  }
};

/**
 * Converts a Date object from react-datepicker back to the correct timestamp
 * for the specified timezone. This reverses the "shifting" done in convertTimestampToPickerDate.
 * @param {Date} pickerDate - Date object from react-datepicker
 * @param {string} timezone - IANA timezone identifier (e.g., 'America/New_York')
 * @param {string} format - Day.js format string to determine picker type
 * @returns {number} - Correct timestamp for storage
 */
export const convertPickerDateToTimestamp = (pickerDate, timezone, format) => {
  // If no date provided, return current timestamp
  if (!pickerDate || !(pickerDate instanceof Date)) {
    return Date.now();
  }

  // If no timezone provided, return date as timestamp
  if (!timezone) {
    return pickerDate.getTime();
  }

  const pickerType = detectPickerType(format);
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const dayjsObj = dayjs(pickerDate).tz(userTimezone);

  // If user is already in the target timezone, no conversion needed
  if (userTimezone === timezone) {
    return pickerDate.getTime();
  }

  switch (pickerType) {
    case 'time': {
      // For time-only: Extract time components and apply to today in target timezone
      const todayInTargetTimezone = dayjs().tz(timezone).startOf('day');
      const adjustedTime = todayInTargetTimezone
        .add(dayjsObj.hour(), 'hours')
        .add(dayjsObj.minute(), 'minutes')
        .add(dayjsObj.second(), 'seconds');
      
      return adjustedTime.valueOf();
    }
    
    case 'date': {
      // For date-only: Convert the date to target timezone at midnight
      const dateInUserTimezone = dayjsObj.startOf('day');
      const adjustedDate = dayjs.tz(dateInUserTimezone.format('YYYY-MM-DD'), timezone);
      
      return adjustedDate.valueOf();
    }
    
    case 'datetime': {
      // For datetime: The picker shows date/time components in user's timezone
      // We need to interpret those same components as being in the target timezone
      const year = dayjsObj.year();
      const month = dayjsObj.month();
      const date = dayjsObj.date();
      const hour = dayjsObj.hour();
      const minute = dayjsObj.minute();
      const second = dayjsObj.second();
      
      // Create a dayjs object in target timezone with these components
      const adjustedDatetime = dayjs().tz(timezone)
        .year(year)
        .month(month)
        .date(date)
        .hour(hour)
        .minute(minute)
        .second(second);
      
      return adjustedDatetime.valueOf();
    }
    
    default:
      return pickerDate.getTime();
  }
};

/**
 * Validates that a custom date/time format string only uses allowed tokens
 * Allowed tokens: MM, DD, YYYY, HH, hh, mm, a
 * Allowed separators: -, /, :, and spaces
 * @param {string} format - The format string to validate
 * @returns {boolean} - true if valid, false otherwise
 */
export const validateDateTimeFormat = (format) => {
  // Check if format is a non-empty string
  if (!format || typeof format !== 'string' || format.trim().length === 0) {
    return false;
  }

  // Define allowed tokens and separators
  const allowedTokens = ['MM', 'DD', 'YYYY', 'HH', 'hh', 'mm', 'a'];
  const allowedSeparators = ['-', '/', ':', ' '];
  
  // Create regex pattern that matches only allowed tokens and separators
  // The pattern matches: MM, DD, YYYY, HH, hh, mm, a, -, /, :, or spaces
  const validTokenPattern = /^(MM|DD|YYYY|HH|hh|mm|a|[-/: ])+$/;
  
  // Check if the entire string is composed of valid tokens and separators
  if (!validTokenPattern.test(format)) {
    return false;
  }
  
  // Ensure at least one actual date/time token exists (not just separators)
  const hasDateOrTimeToken = allowedTokens.some(token => format.includes(token));
  
  return hasDateOrTimeToken;
};

/**
 * Replaces specific format strings in a JoyDoc structure
 * @param {Object} docObject - The JoyDoc object to process
 * @param {Object} replacementFormats - Object containing replacement values
 * @param {string} replacementFormats.dateTime - Replacement for "MM/DD/YYYY hh:mma"
 * @param {string} replacementFormats.date - Replacement for "MM/DD/YYYY"
 * @param {string} replacementFormats.time - Replacement for "hh:mma"
 * @param {string} replacementFormats.defaultFormat - Default format for missing formats
 * @returns {Object} - The original JSON structure with updated format values
 */
export const replaceLegacyDateTimeFormats = (docObject, replacementFormats) => {
  // Create a deep copy to avoid mutating the original object
  const result = JSON.parse(JSON.stringify(docObject));
  
  // Define the format mappings
  const formatMappings = {
    'MM/DD/YYYY hh:mma': replacementFormats.dateTime,
    'MM/DD/YYYY': replacementFormats.date,
    'hh:mma': replacementFormats.time
  };
  
  // Create a lookup map of fields by _id for efficient access
  const fieldsMap = new Map();
  if (result.fields) {
    result.fields.forEach(field => {
      fieldsMap.set(field._id, field);
    });
  }
  
  // Process each file and its pages
  if (result.files) {
    result.files.forEach(file => {
      if (file.pages) {
        file.pages.forEach(page => {
          if (page.fieldPositions) {
            page.fieldPositions.forEach(fieldPosition => {
              
              // 1. Process Date FieldPositions
              if (fieldPosition.type === 'date') {
                if (fieldPosition.format) {
                  // Match existing format and replace if found in mappings
                  if (formatMappings.hasOwnProperty(fieldPosition.format)) {
                    fieldPosition.format = formatMappings[fieldPosition.format];
                  }
                  // If format doesn't match any mapping, leave it unchanged
                } else {
                  // Add default format if no format exists
                  fieldPosition.format = replacementFormats.defaultFormat;
                }
              }
              
              // 2. Process Table FieldPositions
              if (fieldPosition.type === 'table') {
                const associatedField = fieldsMap.get(fieldPosition.field);
                
                if (associatedField && associatedField.tableColumns) {
                  // Initialize tableColumns if it doesn't exist
                  if (!fieldPosition.tableColumns) {
                    fieldPosition.tableColumns = {};
                  }
                  
                  // Get all date columns from the associated field
                  const dateColumns = associatedField.tableColumns.filter(column => 
                    column.type === 'date' && !column.deleted
                  );
                  
                  // Process each date column
                  dateColumns.forEach(column => {
                    const columnId = column._id;
                    
                    if (fieldPosition.tableColumns[columnId]) {
                      // Entry exists - update format if present, add default if missing
                      if (fieldPosition.tableColumns[columnId].format) {
                        // Match and replace existing format
                        if (formatMappings.hasOwnProperty(fieldPosition.tableColumns[columnId].format)) {
                          fieldPosition.tableColumns[columnId].format = formatMappings[fieldPosition.tableColumns[columnId].format];
                        }
                        // If format doesn't match any mapping, leave it unchanged
                      } else {
                        // Add default format if missing
                        fieldPosition.tableColumns[columnId].format = replacementFormats.defaultFormat;
                      }
                    } else {
                      // Entry doesn't exist - create with default format
                      fieldPosition.tableColumns[columnId] = {
                        format: replacementFormats.defaultFormat
                      };
                    }
                  });
                }
              }
            });
          }
        });
      }
    });
  }
  
  return result;
};


